import { ReconciliationFormData } from "@/types/reconciliation";

// Legacy report format (existing)
interface LegacySelectedOptions {
  key: string;
  value: string;
}

// Enhanced report format (new)
interface EnhancedSelectedOptions {
  adjustmentType: string;
  adjustmentData: any;
  dateRange: {
    start: string;
    end: string;
  };
  adjustmentSummary?: string;
  filters?: any;
  adjustmentDetails?: {
    valuePercentage?: string;
    codeChanges?: Array<{
      originalCode: string;
      targetCode: string;
      subEntries?: Array<{
        ch99Original: string;
        ch99Target?: string;
      }>;
    }>;
    rateChanges?: Array<{
      htsCode: string;
      newRate: number;
    }>;
    codeChangeCount?: number;
    rateChangeCount?: number;
  };
}

// Utility to determine if selectedOptions is legacy or enhanced format
export const isLegacySelectedOptions = (selectedOptions: string | any): boolean => {
  if (typeof selectedOptions === 'string') {
    try {
      const parsed = JSON.parse(selectedOptions);
      // Legacy format is array of objects with key/value pairs
      return Array.isArray(parsed) && parsed.length > 0 && 'key' in parsed[0];
    } catch {
      return false;
    }
  }
  // Enhanced format is object with adjustmentType
  return selectedOptions && typeof selectedOptions === 'object' && !('adjustmentType' in selectedOptions);
};

// Parse legacy selectedOptions to extract adjustment type
export const parseLegacySelectedOptions = (selectedOptions: string): {
  adjustmentType?: string;
  adjustmentValue?: string | null;
  summary?: string;
} => {
  try {
    const parsed: LegacySelectedOptions[] = JSON.parse(selectedOptions);
    
    // Find reconciliation type from q1
    const reconciliationType = parsed.find(opt => opt.key === 'q1')?.value;
    
    // Find adjustment value from q5
    const adjustmentValue = parsed.find(opt => opt.key === 'q5')?.value;
    
    // Map legacy reconciliation type to adjustment type
    let adjustmentType = 'unknown';
    if (reconciliationType === 'reconciliation') {
      adjustmentType = 'value_percentage';
    } else if (reconciliationType === 'duty_rate') {
      adjustmentType = 'duty_rate_adjustment';
    }
    
    return {
      adjustmentType,
      adjustmentValue,
      summary: `Legacy ${adjustmentType} adjustment${adjustmentValue ? `: ${adjustmentValue}` : ''}`
    };
  } catch (error) {
    console.error('Error parsing legacy selectedOptions:', error);
    return {};
  }
};

// Parse enhanced selectedOptions to extract key information
export const parseEnhancedSelectedOptions = (selectedOptions: EnhancedSelectedOptions): {
  adjustmentType: string;
  adjustmentValue?: string | null;
  summary?: string;
} => {
  const { adjustmentType, adjustmentData, adjustmentSummary } = selectedOptions;
  
  let adjustmentValue = null;
  
  if (adjustmentData) {
    if ('percentage' in adjustmentData && adjustmentData.percentage) {
      adjustmentValue = adjustmentData.percentage;
    } else if ('amount' in adjustmentData && adjustmentData.amount) {
      adjustmentValue = `${adjustmentData.currency || '$'}${adjustmentData.amount}`;
    } else if ('htsRateChanges' in adjustmentData && adjustmentData.htsRateChanges) {
      const changesCount = Object.keys(adjustmentData.htsRateChanges).length;
      adjustmentValue = `${changesCount} HTS rate changes`;
    }
  }
  
  return {
    adjustmentType,
    adjustmentValue,
    summary: adjustmentSummary || `${adjustmentType} adjustment: ${adjustmentValue}`
  };
};

// Universal parser that handles both formats
export const parseSelectedOptions = (selectedOptions: string | any): {
  adjustmentType: string;
  adjustmentValue: string | null;
  summary: string;
  isLegacy: boolean;
} => {
  const isLegacy = isLegacySelectedOptions(selectedOptions);
  
  if (isLegacy && typeof selectedOptions === 'string') {
    const parsed = parseLegacySelectedOptions(selectedOptions);
    return {
      adjustmentType: parsed.adjustmentType || 'unknown',
      adjustmentValue: parsed.adjustmentValue || null,
      summary: parsed.summary || 'Legacy adjustment',
      isLegacy: true
    };
  } else {
    const parsed = parseEnhancedSelectedOptions(selectedOptions);
    return {
      adjustmentType: parsed.adjustmentType,
      adjustmentValue: parsed.adjustmentValue || null,
      summary: parsed.summary || 'Enhanced adjustment',
      isLegacy: false
    };
  }
};

// Convert enhanced reconciliation form data to selectedOptions format
export const convertFormDataToSelectedOptions = (formData: ReconciliationFormData): EnhancedSelectedOptions => {
  const { adjustmentType, adjustmentData, dateRange, filters } = formData;
  
  let adjustmentSummary = '';
  
  if (adjustmentType === 'value_percentage') {
    if ('percentage' in adjustmentData) {
      adjustmentSummary = `Apply ${adjustmentData.percentage} value adjustment to trade records`;
    } else if ('amount' in adjustmentData) {
      adjustmentSummary = `Apply ${adjustmentData.currency}${adjustmentData.amount} value adjustment to trade records`;
    }
  } else if (adjustmentType === 'duty_rate_adjustment' && 'htsRateChanges' in adjustmentData) {
    const changesCount = Object.keys(adjustmentData.htsRateChanges || {}).length;
    adjustmentSummary = `Update duty rates for ${changesCount} HTS codes`;
  }
  
  return {
    adjustmentType,
    adjustmentData,
    dateRange,
    adjustmentSummary,
    filters
  };
};

// Get display-friendly adjustment type label
export const getAdjustmentTypeLabel = (adjustmentType: string): string => {
  switch (adjustmentType) {
    case 'value_percentage':
      return 'Value Adjustment';
    case 'duty_rate_adjustment':
      return 'Duty Rate Adjustment';
    case 'combined_value_and_duty':
      return 'Combined Value and Duty Adjustment';
    case 'reconciliation':
      return 'Legacy Reconciliation';
    case 'duty_rate':
      return 'Legacy Duty Rate';
    default:
      return 'Unknown';
  }
};

// Format adjustment details for display
export const formatAdjustmentDetails = (adjustmentDetails: any): {
  primaryInfo: string;
  secondaryInfo?: string;
} => {
  if (!adjustmentDetails) {
    return { primaryInfo: 'N/A' };
  }

  const parts: string[] = [];

  // Value percentage
  if (adjustmentDetails.valuePercentage) {
    parts.push(`${adjustmentDetails.valuePercentage} value adjustment`);
  }

  // Code changes - with subentry count if available
  if (adjustmentDetails.codeChangeCount > 0) {
    let codeChangeText = `${adjustmentDetails.codeChangeCount} code change${adjustmentDetails.codeChangeCount > 1 ? 's' : ''}`;
    
    // Count total subentries if available
    if (adjustmentDetails.codeChanges) {
      const totalSubentries = adjustmentDetails.codeChanges.reduce((count: number, change: any) => {
        return count + (change.subEntries ? change.subEntries.length : 0);
      }, 0);
      
      if (totalSubentries > 0) {
        codeChangeText += ` (${totalSubentries} CH99 change${totalSubentries > 1 ? 's' : ''})`;
      }
    }
    
    parts.push(codeChangeText);
  }

  // Rate changes
  if (adjustmentDetails.rateChangeCount > 0) {
    parts.push(`${adjustmentDetails.rateChangeCount} rate change${adjustmentDetails.rateChangeCount > 1 ? 's' : ''}`);
  }

  if (parts.length === 0) {
    return { primaryInfo: 'No adjustments' };
  }

  return {
    primaryInfo: parts[0],
    secondaryInfo: parts.slice(1).join(', ')
  };
};